import { useNavigate } from "@tanstack/react-router";
import { Globe, LogOut, Monitor, Moon, Sun, Truck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import type { DriverDto, UpdateProfileInput } from "@mandoob/shared";

import type { MutationHookOptions } from "@/api/query-helpers";
import { useUpdateProfile } from "@/api/session";
import { WalletCard } from "@/features/billing/wallet-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { clearAccessToken } from "@/config";
import { useLanguage } from "@/contexts/language";
import { useTheme, type ThemeSetting } from "@/contexts/theme";
import { clearStoredLanguage, hasStoredLanguage, setLanguage } from "@/i18n";
import { reportDeviceInfo } from "@/lib/device-info";
import {
  hapticImpact,
  hapticsEnabled,
  hapticsSupported,
  setHapticsEnabled,
} from "@/lib/haptics";
import {
  checkNotificationPermission,
  notificationsEnabled,
  requestNotificationPermission,
  setNotificationsEnabled,
  type NotificationPermission,
} from "@/lib/notifications";
import { pushLogout } from "@/lib/push";
import { playMessageSound, setSoundEnabled, soundEnabled } from "@/lib/sound";
import { errorMessage } from "@/lib/errors";
import {
  formatPhoneNumberForDisplay,
  normalizePhoneInput,
} from "@/lib/phone-utils";

import { clearSession, loadSession, useSession } from "@/session";

type LanguageSetting = "auto" | "ar" | "en";

export function SettingsPage() {
  const { t, dir, language } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const { theme, setTheme } = useTheme();

  const [name, setName] = useState(session?.driver.name ?? "");
  const [company, setCompany] = useState(session?.driver.company ?? "");
  const [callingPhone, setCallingPhone] = useState(
    session?.driver.callingPhone ?? "",
  );
  const [callingPhoneError, setCallingPhoneError] = useState(false);
  // One mutation per field. They all hit PUT /session/profile, but a single
  // shared mutation would put every Save button into the loading state as soon
  // as one of them is clicked.
  const profileMutation: MutationHookOptions<DriverDto, UpdateProfileInput> = {
    mutation: {
      onSuccess: () => {
        toast.success(t("SuccessfullySaved"));
        void loadSession().catch(() => undefined);
      },
      onError: (error) => toast.error(errorMessage(error, t)),
    },
  };
  const saveNameMutation = useUpdateProfile(profileMutation);
  const saveCompanyMutation = useUpdateProfile(profileMutation);
  const saveCallingPhoneMutation = useUpdateProfile(profileMutation);

  const languageSetting: LanguageSetting = hasStoredLanguage()
    ? language
    : "auto";

  const onLanguageChange = (value: string) => {
    if (value === "auto") {
      clearStoredLanguage();
    } else if (value === "ar" || value === "en") {
      setLanguage(value);
    }
    // The driver's language on the server follows what the app shows.
    void reportDeviceInfo();
  };

  // The switch reflects preference AND OS permission: it can only be on when
  // both allow it, so a driver who denied the system prompt sees it off.
  const [notifyPermission, setNotifyPermission] =
    useState<NotificationPermission>("prompt");
  const [notifyPreference, setNotifyPreference] =
    useState(notificationsEnabled);

  useEffect(() => {
    void checkNotificationPermission().then(setNotifyPermission);
  }, []);

  const notifySupported = notifyPermission !== "unsupported";
  const notifyOn = notifyPreference && notifyPermission === "granted";

  const onNotifyChange = async (checked: boolean) => {
    if (!checked) {
      setNotificationsEnabled(false);
      setNotifyPreference(false);
      return;
    }
    // Turning it on from a real tap — the only time iOS/Safari will show the
    // permission prompt.
    let permission = notifyPermission;
    if (permission !== "granted") {
      permission = await requestNotificationPermission();
      setNotifyPermission(permission);
      void reportDeviceInfo();
    }
    if (permission === "granted") {
      setNotificationsEnabled(true);
      setNotifyPreference(true);
    } else {
      toast.error(t("Mandoob:Settings.NotificationsBlocked"));
    }
  };

  // The chime the app plays itself. The sound an OS notification makes comes
  // from its channel instead, which only the device settings can change.
  const [sound, setSound] = useState(soundEnabled);

  const onSoundChange = (checked: boolean) => {
    setSoundEnabled(checked);
    setSound(checked);
    // Turning it on plays it, so the driver hears what they just picked.
    if (checked) playMessageSound();
  };

  // Vibration is a plain local preference — no OS permission involved, so the
  // switch is only hidden on hardware that cannot vibrate at all.
  const [haptics, setHaptics] = useState(hapticsEnabled);

  const onHapticsChange = (checked: boolean) => {
    setHapticsEnabled(checked);
    setHaptics(checked);
    // Turning it on demonstrates what it feels like.
    if (checked) hapticImpact("medium");
  };

  const logout = () => {
    // Detach the device from this driver first — otherwise their pushes keep
    // arriving on a phone that is now signed out.
    void pushLogout();
    clearAccessToken();
    clearSession();
    void navigate({ to: "/login" });
  };

  const nameDirty =
    name.trim() !== (session?.driver.name ?? "") && !!name.trim();

  const companyDirty = company.trim() !== (session?.driver.company ?? "");
  const callingPhoneDirty =
    callingPhone.trim() !== (session?.driver.callingPhone ?? "");

  const saveCompany = () => {
    // Empty clears the default company.
    saveCompanyMutation.mutate({ company: company.trim() || null });
  };

  const saveCallingPhone = () => {
    const trimmed = callingPhone.trim();
    if (!trimmed) {
      saveCallingPhoneMutation.mutate({ callingPhone: null });
      return;
    }
    const normalized = normalizePhoneInput(trimmed);
    if (!normalized) {
      setCallingPhoneError(true);
      toast.error(t("Mandoob:Form.InvalidPhone"));
      return;
    }
    saveCallingPhoneMutation.mutate({ callingPhone: normalized });
  };

  return (
    <div dir={dir} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
      <div className="tw:mx-auto tw:flex tw:w-full tw:flex-col tw:gap-4 tw:px-4 tw:pb-6 tw:pt-[calc(20px+env(safe-area-inset-top))] tw:md:max-w-3xl tw:md:px-8 tw:md:pt-8">
        <h1 className="tw:m-0 tw:text-xl tw:font-semibold">
          {t("Mandoob:Settings.Title")}
        </h1>

        <Card>
          <CardHeader>
            <CardTitle>{t("Mandoob:Settings.Profile")}</CardTitle>
          </CardHeader>
          <CardContent className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label htmlFor="driver-name">{t("Mandoob:Settings.Name")}</Label>
              <div className="tw:flex tw:gap-2">
                <Input
                  id="driver-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={120}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveNameMutation.mutate({ name: name.trim() })}
                  disabled={!nameDirty}
                  loading={saveNameMutation.isPending}
                >
                  {t("Save")}
                </Button>
              </div>
            </div>
            {session?.driver.phone ? (
              <div className="tw:flex tw:flex-col tw:gap-2">
                <Label>{t("Mandoob:Settings.Phone")}</Label>
                <Input
                  value={formatPhoneNumberForDisplay(session.driver.phone)}
                  readOnly
                  disabled
                  dir="ltr"
                  className="tw:text-start"
                />
              </div>
            ) : null}
            {session?.driver.email ? (
              <div className="tw:flex tw:flex-col tw:gap-2">
                <Label>{t("Mandoob:Settings.Email")}</Label>
                <Input
                  value={session.driver.email}
                  readOnly
                  disabled
                  dir="ltr"
                  className="tw:text-start"
                />
              </div>
            ) : null}
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              {t("Mandoob:Settings.LoginIdentifierHint")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="tw:flex tw:items-center tw:gap-2">
              <Truck className="tw:size-4" strokeWidth={1.75} />
              {t("Mandoob:Settings.DeliveryDefaults")}
            </CardTitle>
          </CardHeader>
          <CardContent className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label htmlFor="driver-company">
                {t("Mandoob:Delivery.Company")}
              </Label>
              <div className="tw:flex tw:gap-2">
                <Input
                  id="driver-company"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  maxLength={120}
                  autoComplete="organization"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveCompany}
                  disabled={!companyDirty}
                  loading={saveCompanyMutation.isPending}
                >
                  {t("Save")}
                </Button>
              </div>
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                {t("Mandoob:Delivery.CompanyHint")}
              </p>
            </div>
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label htmlFor="driver-calling-phone">
                {t("Mandoob:Delivery.CallingPhone")}
              </Label>
              <div className="tw:flex tw:gap-2">
                <Input
                  id="driver-calling-phone"
                  value={callingPhone}
                  onChange={(event) => {
                    setCallingPhone(event.target.value);
                    setCallingPhoneError(false);
                  }}
                  inputMode="tel"
                  dir="ltr"
                  autoComplete="tel"
                  aria-invalid={callingPhoneError || undefined}
                  className="tw:text-start"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveCallingPhone}
                  disabled={!callingPhoneDirty}
                  loading={saveCallingPhoneMutation.isPending}
                >
                  {t("Save")}
                </Button>
              </div>
              {callingPhoneError ? (
                <p className="tw:m-0 tw:text-xs tw:text-destructive">
                  {t("Mandoob:Form.InvalidPhone")}
                </p>
              ) : (
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:Settings.DeliveryDefaultsHint")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("Mandoob:Settings.Preferences")}</CardTitle>
          </CardHeader>
          <CardContent className="tw:flex tw:flex-col tw:gap-4">
            {notifySupported && (
              <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Label htmlFor="notifications">
                    {t("Mandoob:Settings.Notifications")}
                  </Label>
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    {t("Mandoob:Settings.NotificationsHint")}
                  </p>
                </div>
                <Switch
                  id="notifications"
                  checked={notifyOn}
                  onCheckedChange={(checked) => void onNotifyChange(checked)}
                />
              </div>
            )}
            <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="sound">{t("Mandoob:Settings.Sound")}</Label>
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {t("Mandoob:Settings.SoundHint")}
                </p>
              </div>
              <Switch
                id="sound"
                checked={sound}
                onCheckedChange={onSoundChange}
              />
            </div>
            {hapticsSupported() && (
              <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Label htmlFor="haptics">
                    {t("Mandoob:Settings.Haptics")}
                  </Label>
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    {t("Mandoob:Settings.HapticsHint")}
                  </p>
                </div>
                <Switch
                  id="haptics"
                  checked={haptics}
                  onCheckedChange={onHapticsChange}
                />
              </div>
            )}
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
              <Label>{t("Mandoob:Settings.Language")}</Label>
              <ToggleGroup
                type="single"
                value={languageSetting}
                onValueChange={(value) => value && onLanguageChange(value)}
              >
                <ToggleGroupItem
                  value="auto"
                  aria-label={t("Mandoob:Settings.Auto")}
                  title={t("Mandoob:Settings.Auto")}
                >
                  <Globe strokeWidth={1.75} />
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="ar"
                  aria-label="العربية"
                  title="العربية"
                >
                  ع
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="en"
                  aria-label="English"
                  title="English"
                >
                  EN
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
              <Label>{t("Mandoob:Settings.Theme")}</Label>
              <ToggleGroup
                type="single"
                value={theme}
                onValueChange={(value) =>
                  value && setTheme(value as ThemeSetting)
                }
              >
                <ToggleGroupItem
                  value="auto"
                  aria-label={t("Mandoob:Settings.Auto")}
                  title={t("Mandoob:Settings.Auto")}
                >
                  <Monitor strokeWidth={1.75} />
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="light"
                  aria-label={t("Mandoob:Settings.Light")}
                  title={t("Mandoob:Settings.Light")}
                >
                  <Sun strokeWidth={1.75} />
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="dark"
                  aria-label={t("Mandoob:Settings.Dark")}
                  title={t("Mandoob:Settings.Dark")}
                >
                  <Moon strokeWidth={1.75} />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>

        <WalletCard />

        <Button
          type="button"
          variant="outline"
          onClick={logout}
          className="tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive"
        >
          <LogOut className="tw:size-5" />
          {t("Mandoob:Settings.Logout")}
        </Button>
      </div>
    </div>
  );
}
